import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface JoinModalProps {
  isOpen: boolean;
  isSubstitute: boolean;
  onClose: () => void;
  onSubmit: (formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => void;
  isLoading: boolean;
  isGuest?: boolean;
  onLoginClick?: () => void;
  isCreatorAddingGuest?: boolean;
}

export const JoinModal: React.FC<JoinModalProps> = ({
  isOpen,
  isSubstitute,
  onClose,
  onSubmit,
  isLoading,
  isGuest = false,
  onLoginClick,
  isCreatorAddingGuest = false
}) => {
  const { user, loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    position: user?.position || 'Mediocampista',
    rating: user?.rating || 3,
    isSubstitute: isSubstitute
  });

  // Actualizar formulario cuando el usuario cambie
  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        position: user.position || 'Mediocampista',
        rating: user.rating || 3,
        isSubstitute: isSubstitute
      });
    }
  }, [user, isSubstitute]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      name: user?.name || '',
      position: user?.position || 'Mediocampista',
      rating: user?.rating || 3,
      isSubstitute: isSubstitute
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px',
      boxSizing: 'border-box',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '18px',
        padding: '20px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxSizing: 'border-box',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        border: '1px solid #33383B'
      }}>
        <h3 style={{ 
          color: '#00E676',
          marginBottom: '20px',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          letterSpacing: '0.08em'
        }}>
          {isCreatorAddingGuest 
            ? (isSubstitute ? 'Agregar Suplente Invitado' : 'Agregar Jugador Invitado')
            : (isSubstitute ? 'Unirse como Suplente' : 'Unirse al Partido')
          }
        </h3>
        {isGuest && (
          <button
            type="button"
            onClick={() => {
              // Guardar datos del form antes de login
              if (onLoginClick) onLoginClick();
              // Ejecutar login con Google
              loginWithGoogle();
            }}
            className="google-btn"
            style={{
              marginBottom: 16
            }}
          >
            Iniciar sesión con Google
          </button>
        )}
        
        {isSubstitute && (
          <div style={{
            backgroundColor: '#1E2124',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            border: '1px solid #40444B'
          }}>
            <div style={{
              color: '#FF9800',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              🔄 Información sobre suplentes:
            </div>
            <div style={{
              color: '#CCCCCC',
              fontSize: '13px',
              lineHeight: '1.4'
            }}>
              Como suplente, serás promovido automáticamente a jugador principal cuando alguien se baje del partido. Recibirás notificaciones en WhatsApp para confirmar tu promoción.
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="join-modal-name" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600'
            }}>
              Tu nombre:
            </label>
            <input
              id="join-modal-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Tu nombre"
              required
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #33383B',
                backgroundColor: '#181A1B',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="join-modal-position" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600'
            }}>
              Posición:
            </label>
            <select
              id="join-modal-position"
              value={formData.position}
              onChange={(e) => setFormData({...formData, position: e.target.value})}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #33383B',
                backgroundColor: '#181A1B',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
            >
              <option value="Arquero">🥅 Arquero</option>
              <option value="Defensor">🛡️ Defensor</option>
              <option value="Mediocampista">⚽ Mediocampista</option>
              <option value="Delantero">🚀 Delantero</option>
            </select>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <p id="join-modal-rating-label" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              margin: '0 0 8px 0'
            }}>
              Nivel (1-5 estrellas):
            </p>
            <div role="group" aria-labelledby="join-modal-rating-label" style={{ display: 'flex', gap: '8px' }}>
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setFormData({...formData, rating})}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: formData.rating >= rating ? '#FFD600' : '#33383B',
                    color: formData.rating >= rating ? '#181A1B' : '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.isSubstitute}
                onChange={(e) => setFormData({...formData, isSubstitute: e.target.checked})}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#00E676'
                }}
              />
              {isCreatorAddingGuest ? 'Agregar como suplente' : 'Unirse como suplente'}
            </label>
            <p style={{
              color: '#B0B0B0',
              fontSize: '12px',
              marginTop: '4px',
              marginLeft: '26px'
            }}>
              Los suplentes pueden entrar al partido cuando haya espacio disponible
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                flex: 1,
                padding: '14px 16px',
                minHeight: '48px',
                backgroundColor: '#33383B',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600'
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '14px 16px',
                minHeight: '48px',
                backgroundColor: isLoading ? '#6C757D' : '#00E676',
                color: isLoading ? '#FFFFFF' : '#181A1B',
                border: 'none',
                borderRadius: '12px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: '600'
              }}
            >
              {isLoading ? (isCreatorAddingGuest ? 'Agregando...' : 'Uniéndose...') : (isCreatorAddingGuest ? 'Agregar' : 'Unirse')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 